"use client";

import { useRef } from "react";
import Image from "next/image";

export type SlideItem = {
  id: number | string;
  src: string;
  alt: string;
  label: string;
  subtitle?: string | null;
  rating?: string | null;
  href?: string;
};

export default function ImageSlider({ items }: { items: SlideItem[] }) {
  const trackRef = useRef<HTMLDivElement>(null);

  function scrollByCard(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("a");
    const cardWidth = card ? card.getBoundingClientRect().width + 16 : 240;
    track.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  }

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, index) => (
          <a
            key={item.id}
            href={item.href ?? `#sample-${item.id}`}
            className="group relative w-44 flex-shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-[#0f172a]/80"
          >
            <div
              className={`absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r ${
                [
                  "from-violet-500 to-fuchsia-500",
                  "from-blue-500 to-cyan-500",
                  "from-emerald-500 to-teal-500",
                  "from-pink-500 to-rose-500",
                ][index % 4]
              } opacity-0 transition group-hover:opacity-100`}
            />
            <div className="relative h-56 w-full bg-slate-100 dark:bg-slate-800">
              <Image
                src={item.src}
                alt={item.alt}
                fill
                className="object-cover object-top"
                unoptimized
              />
            </div>
            <div className="p-3">
              <p className="truncate text-xs font-semibold text-slate-900 dark:text-white">
                {item.label}
              </p>
              {item.subtitle ? (
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {item.subtitle}
                </p>
              ) : null}
              {item.rating ? (
                <p className="mt-1 text-xs font-medium text-amber-500">
                  ★ {item.rating}
                </p>
              ) : null}
            </div>
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={() => scrollByCard(-1)}
        aria-label="Previous"
        className="absolute -left-4 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-50 sm:flex dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={() => scrollByCard(1)}
        aria-label="Next"
        className="absolute -right-4 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-50 sm:flex dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
      >
        ›
      </button>
    </div>
  );
}
